import React, { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, Play, Pause, RotateCcw, Settings } from 'lucide-react';
import { useSpatialNavigation } from '../hooks/useSpatialNavigation';

interface PlayerControlsProps {
  isPlaying: boolean;
  onPlay: () => void;
  onPause: () => void;
  onStop: () => void;
  onPrevious: () => void;
  onNext: () => void;
  currentIndex: number;
  totalItems: number;
  onSettingsClick: () => void;
}

const PlayerControls: React.FC<PlayerControlsProps> = ({
  isPlaying,
  onPlay,
  onPause,
  onStop,
  onPrevious,
  onNext,
  currentIndex,
  totalItems,
  onSettingsClick
}) => {
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);
  const longPressRef = useRef(false);
  
  const controlsRef = useSpatialNavigation({
    onArrowLeft: onPrevious,
    onArrowRight: onNext,
    onEnter: isPlaying ? onPause : onPlay,
    onPlay: onPlay,
    onPause: onPause,
    onStop: onStop
  });

  const handleStartOverStart = () => {
    longPressRef.current = false;
    const timer = setTimeout(() => {
      longPressRef.current = true;
      onStop(); // This will reset to first media
    }, 500); // 500ms for long press
    setLongPressTimer(timer);
  };

  const handleStartOverEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    if (!longPressRef.current) {
      // Short press - do nothing
    }
  };

  const handleStartOverClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Only handle if it's not a long press
    if (!longPressRef.current) {
      // Do nothing for short press
    }
  };

  const handleStartOverTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    handleStartOverStart();
  };

  const handleStartOverTouchEnd = (e: React.TouchEvent) => {
    e.preventDefault();
    handleStartOverEnd();
  };

  return (
    <div ref={controlsRef} className="player-controls">
      <button 
        className="control-btn settings-btn tooltip-container"
        onClick={onSettingsClick}
        aria-label="Settings"
        title="Click to open the Settings"
      >
        <Settings size={20} />
        <div className="tooltip">Click to open the Settings</div>
      </button>
      
      <button 
        className="control-btn prev-btn tooltip-container"
        onClick={onPrevious}
        disabled={totalItems === 0}
        aria-label="Previous"
        title="Click to go to the previous media"
      >
        <ChevronLeft size={24} />
        <div className="tooltip">Click to go to the previous media</div>
      </button>
      
      <div className="play-controls">
        {isPlaying ? (
          <button 
            className="control-btn pause-btn tooltip-container"
            onClick={onPause}
            aria-label="Pause"
            title="Click to pause the slideshow"
          >
            <Pause size={24} />
            <div className="tooltip">Click to pause the slideshow</div>
          </button>
        ) : (
          <button 
            className="control-btn play-btn tooltip-container"
            onClick={onPlay}
            aria-label="Play"
            title="Click to start the slideshow"
          >
            <Play size={24} />
            <div className="tooltip">Click to start the slideshow</div>
          </button>
        )}
        
        <button 
          className="control-btn start-over-btn tooltip-container"
          onMouseDown={handleStartOverStart}
          onMouseUp={handleStartOverEnd}
          onMouseLeave={handleStartOverEnd}
          onTouchStart={handleStartOverTouchStart}
          onTouchEnd={handleStartOverTouchEnd}
          onTouchCancel={handleStartOverTouchEnd}
          onClick={handleStartOverClick}
          aria-label="Start Over"
          title="Long press to start over"
        >
          <RotateCcw size={20} />
          <div className="tooltip">Long press to start over</div>
        </button>
      </div>
      
      <button 
        className="control-btn next-btn tooltip-container"
        onClick={onNext}
        disabled={totalItems === 0}
        aria-label="Next"
        title="Click to go to the next media"
      >
        <ChevronRight size={24} />
        <div className="tooltip">Click to go to the next media</div>
      </button>
      
      <div className="media-counter">
        {currentIndex + 1} / {totalItems}
      </div>
    </div>
  );
};

export default PlayerControls;
